import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Briefcase, DollarSign, Building2, ArrowRight, Loader2, Sparkles, BellRing, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const TYPES = ["All", "Full-time", "Part-time", "Contract", "Temporary", "Internship"];
const REMOTE_OPTIONS = [
    { value: "all", label: "Any workplace" },
    { value: "remote", label: "Remote" },
    { value: "hybrid", label: "Hybrid" },
    { value: "on_site", label: "On-site" },
];

const Careers = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [type, setType] = useState("All");
    const [location, setLocation] = useState("");
    const [remote, setRemote] = useState("all");
    // iter 149.6 — pay-range filter. 0 means "any pay" (default).
    const [minPay, setMinPay] = useState(0);
    const [savingAlert, setSavingAlert] = useState(false);
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const saveAsAlert = async () => {
        if (authLoading) return; // wait for auth to hydrate before deciding
        if (!user) {
            toast.info("Sign in as talent to save Job Alerts.");
            navigate("/login?next=/careers");
            return;
        }
        if (user.role !== "professional") {
            toast.error("Job Alerts are for talent accounts.");
            return;
        }
        setSavingAlert(true);
        try {
            const { data } = await api.post("/talent/job-alerts", {
                keyword: q, location, remote_status: remote, employment_type: type, frequency: "weekly",
            });
            if (data.duplicate) toast.info("You already have this alert.");
            else toast.success("Alert saved — we'll email you weekly matches.");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSavingAlert(false); }
    };

    const isFeatured = (job) => job.featured_until && new Date(job.featured_until) > new Date();

    useEffect(() => {
        setLoading(true);
        // iter 149.6 — pay filter goes through the backend query so /jobs
        // stays authoritative and DOE postings are excluded server-side.
        const params = {};
        if (minPay > 0) params.min_pay = minPay;
        api.get("/jobs", { params })
            .then(({ data }) => setJobs(data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [minPay]);

    const filtered = useMemo(() => {
        return jobs.filter((j) => {
            const matchesType = type === "All" || j.employment_type === type;
            const matchesLoc = !location || (j.location || "").toLowerCase().includes(location.toLowerCase());
            const matchesQ = !q || `${j.title} ${j.company} ${j.description}`.toLowerCase().includes(q.toLowerCase());
            const matchesRemote =
                remote === "all" ||
                (j.remote_status || "").toLowerCase() === remote ||
                (remote === "remote" && /remote/i.test(j.location || ""));
            return matchesType && matchesLoc && matchesQ && matchesRemote && j.is_active !== false;
        });
    }, [jobs, q, type, location, remote]);

    const industries = useMemo(() => {
        const set = new Set(jobs.map((j) => j.industry).filter(Boolean));
        return Array.from(set);
    }, [jobs]);

    return (
        <div data-testid="careers-page" className="bg-background">
            {/* Hero */}
            <section className="bg-brand-navy dark:bg-slate-900 text-white pt-20 pb-16">
                <div className="container-page max-w-4xl">
                    <p className="overline !text-brand-green">Open Roles</p>
                    <h1 className="mt-3 font-heading font-medium text-4xl lg:text-6xl tracking-tight leading-[1.05]">
                        Curated roles from <span className="text-brand-green">Simonara employers.</span>
                    </h1>
                    <p className="mt-6 text-white/80 text-lg max-w-2xl leading-relaxed">
                        Every role here is hand-selected by our team. Apply directly — your application lands in Simonara&apos;s verified talent library and gets AI-matched to future roles too.
                    </p>
                    <div className="mt-8 flex items-center gap-2 text-sm text-white/70">
                        <Sparkles size={14} className="text-brand-green" />
                        <span>{jobs.length} live role{jobs.length === 1 ? "" : "s"} · Updated by Simonara admins</span>
                    </div>
                </div>
            </section>

            {/* Filters */}
            <section className="border-b border-border bg-card sticky top-20 z-20">
                <div className="container-page py-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-5 relative">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            data-testid="careers-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search title, company, keyword…"
                            className="pl-10 h-11 bg-background"
                        />
                    </div>
                    <div className="md:col-span-3 relative">
                        <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            data-testid="careers-location"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            placeholder="City / Remote"
                            className="pl-10 h-11 bg-background"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Select value={remote} onValueChange={setRemote}>
                            <SelectTrigger data-testid="careers-remote" className="h-11 bg-background">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {REMOTE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger data-testid="careers-type" className="h-11 bg-background">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="container-page pb-3 flex items-center justify-between flex-wrap gap-2">
                    {/* iter 149.6 — Pay-floor chip filter. Chips instead of a
                        slider so the applicant sees the exact thresholds at
                        a glance. "$0+" resets. */}
                    <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Minimum pay" data-testid="careers-pay-chips">
                        <span className="text-xs text-muted-foreground mr-1">Min pay</span>
                        {[
                            { v: 0, label: "Any" },
                            { v: 50000, label: "$50k+" },
                            { v: 75000, label: "$75k+" },
                            { v: 100000, label: "$100k+" },
                            { v: 150000, label: "$150k+" },
                            { v: 200000, label: "$200k+" },
                        ].map((chip) => {
                            const active = minPay === chip.v;
                            return (
                                <button
                                    key={chip.v}
                                    type="button"
                                    onClick={() => setMinPay(chip.v)}
                                    aria-pressed={active}
                                    data-testid={`careers-pay-chip-${chip.v}`}
                                    className={`rounded-full px-3 py-1 text-[11px] font-semibold border transition ${active
                                        ? "bg-emerald-500 border-emerald-500 text-white"
                                        : "bg-transparent border-border text-muted-foreground hover:border-emerald-500/40 hover:text-foreground"}`}
                                >
                                    {chip.label}
                                </button>
                            );
                        })}
                    </div>
                    <span className="text-xs text-muted-foreground">
                        {loading ? "Loading roles…" : `${filtered.length} role${filtered.length === 1 ? "" : "s"} matching your filters`}
                    </span>
                    <button
                        type="button"
                        onClick={saveAsAlert}
                        disabled={savingAlert || authLoading}
                        data-testid="careers-save-alert"
                        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-1.5 text-xs font-semibold hover:bg-emerald-500/15 disabled:opacity-60"
                    >
                        {savingAlert ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />}
                        Save as Job Alert
                    </button>
                </div>
            </section>

            {/* Jobs list */}
            <section className="container-page py-14">
                {loading ? (
                    <div className="py-24 flex flex-col items-center text-muted-foreground">
                        <Loader2 size={22} className="animate-spin mb-3" /> Loading open roles…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="card-soft p-14 text-center">
                        <Briefcase size={26} className="mx-auto text-emerald-500" />
                        <h3 className="mt-4 font-heading font-semibold text-xl text-foreground">No open roles match those filters.</h3>
                        <p className="mt-2 text-sm text-muted-foreground">Try clearing your search — or build a Talent Passport so we can match you the moment the right role opens.</p>
                        <Link to="/talent" className="btn-green mt-6 inline-flex text-sm">Build Talent Passport</Link>
                    </div>
                ) : (
                    <>
                        {industries.length > 0 && (
                            <div className="mb-6 flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                                <span className="uppercase tracking-wider font-semibold">Industries:</span>
                                {industries.map((i) => (
                                    <Badge key={i} variant="outline">{i}</Badge>
                                ))}
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filtered.map((job, idx) => (
                                <motion.article
                                    key={job.id}
                                    data-testid={`job-card-${job.id}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.35, delay: Math.min(idx * 0.02, 0.2) }}
                                    className="card-soft p-6 flex flex-col group hover:border-emerald-500/40 transition-colors"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <h3 className="font-heading font-semibold text-lg text-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors leading-snug">
                                                <Link to={`/jobs/${job.slug || job.id}`}>{job.title}</Link>
                                            </h3>
                                            <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                                                <Building2 size={13} className="text-emerald-500" />
                                                {job.company_slug ? (
                                                    <Link to={`/companies/${job.company_slug}`} className="hover:text-foreground hover:underline">{job.company}</Link>
                                                ) : job.company}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            {isFeatured(job) && (
                                                <Badge data-testid={`featured-badge-${job.id}`} className="bg-amber-500/20 text-amber-700 dark:text-amber-400 border-0 text-[10px] font-bold">
                                                    <Zap size={9} className="inline mr-0.5" /> FEATURED
                                                </Badge>
                                            )}
                                            <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 border text-[10px]">{job.employment_type}</Badge>
                                            {job.remote_status && job.remote_status !== "on_site" && (
                                                <Badge variant="outline" className="text-[10px] capitalize">{job.remote_status}</Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                                        <span className="inline-flex items-center gap-1"><MapPin size={12} /> {job.location}</span>
                                        {job.salary && <span className="inline-flex items-center gap-1"><DollarSign size={12} /> {job.salary}</span>}
                                        {job.industry && <span className="inline-flex items-center gap-1"><Briefcase size={12} /> {job.industry}</span>}
                                    </div>
                                    {job.description && (
                                        <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-3">{job.description}</p>
                                    )}
                                    <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                                        <span className="text-[11px] text-muted-foreground">Posted {job.posted_at ? new Date(job.posted_at).toLocaleDateString() : "recently"}</span>
                                        <Link to={`/jobs/${job.slug || job.id}`} data-testid={`view-job-${job.id}`} className="text-sm font-semibold text-brand-green dark:text-emerald-400 group-hover:underline inline-flex items-center gap-1">
                                            View & apply <ArrowRight size={13} />
                                        </Link>
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

export default Careers;
